import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Archive, Pencil, Plus, X } from "lucide-react";
import { Row } from "../Common/Row";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { usePortalScope } from "@/analytics/PortalScope";
import {
  LEARNING_KINDS,
  type AppliesTo,
  type Learning,
  type LearningPatch,
} from "../../api/conversationKnowledgeApi";

const KIND_LABEL: Record<string, string> = {
  rule: "Rule",
  procedure: "Procedure",
  fact: "Fact",
  qa: "Q&A",
};

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

function valueText(value: string | string[]): string {
  return Array.isArray(value) ? value.join(", ") : value;
}

/** applies_to as editable rows. A comma in a value the user typed means a
 *  list. A row keeps its original value until the user edits that value, so
 *  the comma rule never rewrites what they did not touch (a value that
 *  itself contains a comma, a one-element list). */
interface EntityDraft {
  key: string;
  value: string;
  /** The stored value; absent for a row the user added. */
  original?: string | string[];
  /** The user edited `value`. */
  touched: boolean;
}

function toDrafts(appliesTo: AppliesTo): EntityDraft[] {
  return Object.entries(appliesTo ?? {}).map(([key, value]) => ({
    key,
    value: valueText(value),
    original: value,
    touched: false,
  }));
}

function draftValue(d: EntityDraft): string | string[] | null {
  if (!d.touched && d.original !== undefined) return d.original;
  const parts = d.value.split(",").map((v) => v.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  // A value that was a list stays a list, even of one.
  return parts.length > 1 || Array.isArray(d.original) ? parts : parts[0]!;
}

function fromDrafts(drafts: EntityDraft[]): AppliesTo {
  const out: AppliesTo = {};
  for (const d of drafts) {
    const k = d.key.trim();
    const value = draftValue(d);
    if (!k || value === null) continue;
    out[k] = value;
  }
  return out;
}

export function AppliesToChips({ appliesTo }: { appliesTo: AppliesTo }) {
  const entries = Object.entries(appliesTo ?? {});
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Applies to everything
      </Typography>
    );
  }
  return (
    <Row gap={0.5} align="center" wrap>
      {entries.map(([key, value]) => (
        <Chip key={key} size="small" variant="outlined" label={`${key}: ${valueText(value)}`} />
      ))}
    </Row>
  );
}

/** One learning: read view with Edit / Retire, or the inline edit form. The
 *  text is model-written from user input, so it renders as plain text only. */
export function LearningRow({
  learning,
  editable,
  onSave,
  onRetire,
}: {
  learning: Learning;
  /** The document's can_write; cosmetic — the server re-checks. */
  editable: boolean;
  onSave: (patch: LearningPatch) => Promise<void>;
  onRetire: () => Promise<void>;
}) {
  const retireScope = usePortalScope("learning-retire_dialog")["data-ph-scope"];
  // The kind menu is portaled out of the row, so it names its path (D7).
  const kindMenuScope = usePortalScope("learning-kind");
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(learning.text);
  const [kind, setKind] = useState<string>(learning.kind);
  const [entities, setEntities] = useState<EntityDraft[]>(() => toDrafts(learning.applies_to));
  // Any add / remove / key or value edit; untouched, applies_to is never sent.
  const [entitiesDirty, setEntitiesDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const retired = learning.status === "retired";

  const startEdit = () => {
    setText(learning.text);
    setKind(learning.kind);
    setEntities(toDrafts(learning.applies_to));
    setEntitiesDirty(false);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    // Only what changed: the server versions every edit.
    const patch: LearningPatch = {};
    if (text.trim() !== learning.text.trim()) patch.text = text.trim();
    if (kind !== learning.kind) patch.kind = kind;
    if (entitiesDirty) {
      const nextApplies = fromDrafts(entities);
      if (JSON.stringify(nextApplies) !== JSON.stringify(learning.applies_to ?? {})) {
        patch.applies_to = nextApplies;
      }
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(patch);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const setEntity = (i: number, field: "key" | "value", value: string) => {
    setEntitiesDirty(true);
    setEntities((prev) =>
      prev.map((d, j) =>
        j !== i ? d : field === "key" ? { ...d, key: value } : { ...d, value, touched: true },
      ),
    );
  };

  if (editing) {
    return (
      <Box
        component="li"
        data-ph-scope="learning_edit"
        sx={{ listStyle: "none", p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
      >
        <TextField
          label="Learning"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
        />
        <FormControl size="small" sx={{ minWidth: 160, mb: 1.5 }}>
          <InputLabel id={`learning-kind-${learning.id}`}>Kind</InputLabel>
          <Select
            labelId={`learning-kind-${learning.id}`}
            label="Kind"
            value={kind}
            onChange={(e) => setKind(String(e.target.value))}
            data-ph="open"
            MenuProps={{ slotProps: { list: kindMenuScope } }}
          >
            {LEARNING_KINDS.map((k) => (
              <MenuItem key={k} value={k} data-ph="select">
                {kindLabel(k)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
          Applies to (separate several values with commas; none means everything)
        </Typography>
        {entities.map((d, i) => (
          <Row key={i} gap={1} align="center" sx={{ mb: 1 }}>
            <TextField
              size="small"
              label="Entity"
              value={d.key}
              onChange={(e) => setEntity(i, "key", e.target.value)}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="Value"
              value={d.value}
              onChange={(e) => setEntity(i, "value", e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              aria-label={`remove ${d.key || "entity"}`}
              data-ph="delete_entity"
              onClick={() => {
                setEntitiesDirty(true);
                setEntities((prev) => prev.filter((_, j) => j !== i));
              }}
            >
              <X size={14} />
            </IconButton>
          </Row>
        ))}
        <Button
          size="small"
          startIcon={<Plus size={14} />}
          onClick={() => {
            setEntitiesDirty(true);
            setEntities((prev) => [...prev, { key: "", value: "", touched: true }]);
          }}
          data-ph="create_entity"
          sx={{ mb: 1 }}
        >
          Add entity
        </Button>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Row gap={1} justify="end">
          <Button size="small" onClick={() => setEditing(false)} disabled={saving} data-ph="cancel">
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void save()}
            disabled={saving || !text.trim()}
            data-ph="save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Row>
      </Box>
    );
  }

  return (
    <Box
      component="li"
      data-ph-scope="learning"
      sx={{ listStyle: "none", py: 1, borderBottom: 1, borderColor: "divider", opacity: retired ? 0.6 : 1 }}
    >
      <Row gap={1} align="start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Row gap={1} align="center" sx={{ mb: 0.5 }}>
            <Chip size="small" label={kindLabel(learning.kind)} />
            {learning.status === "edited" && (
              <Typography variant="caption" color="text.secondary">
                Edited
              </Typography>
            )}
            {retired && (
              <Typography variant="caption" color="text.secondary">
                Retired
              </Typography>
            )}
          </Row>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}>
            {learning.text}
          </Typography>
          <AppliesToChips appliesTo={learning.applies_to} />
        </Box>
        {editable && !retired && (
          <Row gap={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label="edit learning" data-ph="edit" onClick={startEdit}>
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Retire">
              <IconButton
                size="small"
                aria-label="retire learning"
                data-ph="delete"
                onClick={() => setConfirmRetire(true)}
              >
                <Archive size={14} />
              </IconButton>
            </Tooltip>
          </Row>
        )}
      </Row>
      <ConfirmDialog
        open={confirmRetire}
        title="Retire this learning?"
        body="The assistant will stop using it. The conversation itself stays in your knowledge."
        confirmLabel="Retire"
        destructive
        onConfirm={async () => {
          await onRetire();
          setConfirmRetire(false);
        }}
        onClose={() => setConfirmRetire(false)}
        scope={retireScope}
      />
    </Box>
  );
}
